import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { collectionData, Firestore, collection } from '@angular/fire/firestore';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { take, timeout } from 'rxjs';
import { AdminSectionBaseComponent } from 'src/app/shared/admin-section-base';
import {
  AdminApiService,
  EnvironmentNotebooks,
  UserItem,
  UserListPage,
  UserMaintenanceType,
  UserUsage,
} from 'src/app/shared/services/admin-api.service';

interface ProfileTypeOption {
  id: string;
  maxCredits: number;
  maxMachines: number;
  suspendHours: number;
  maxIdleDays: number;
}

interface RawProfileTypeOption {
  id?: unknown;
  maxCredits?: unknown;
  maxMachines?: unknown;
  suspendHours?: unknown;
  maxIdleDays?: unknown;
}

type UserFilterField = 'email' | 'id';
type UserFilterValueOption = { value: string; label: string };

type ExtendedUserFilterField = 'email' | 'id' | 'company' | 'role' | 'status' | 'profileType';

@Component({
  standalone: false,
  selector: 'app-manage-users',
  templateUrl: './manage-users.component.html',
  styleUrls: ['./manage-users.component.scss'],
})
export class ManageUsersComponent extends AdminSectionBaseComponent implements OnInit, OnDestroy {
  users: UserItem[] = [];
  usersFilterField: ExtendedUserFilterField = 'email';
  usersFilterValue = '';
  userFilterValueOptions: UserFilterValueOption[] = [];
  usersPage = 1;
  usersTotalItems = 0;
  usersTotalPages = 1;
  readonly usersPageSize = 20;
  readonly userFilterOptions: Array<{ value: ExtendedUserFilterField; label: string }> = [
    { value: 'email', label: 'Email' },
    { value: 'id', label: 'ID' },
    { value: 'company', label: 'Company' },
    { value: 'role', label: 'Role' },
    { value: 'status', label: 'Status' },
    { value: 'profileType', label: 'Profile Type' },
  ];
  readonly userRoleFilterOptions: UserFilterValueOption[] = [
    { value: 'user', label: 'User' },
    { value: 'admin', label: 'Admin' },
  ];
  readonly userStatusFilterOptions: UserFilterValueOption[] = [
    { value: 'active', label: 'Active' },
    { value: 'suspended', label: 'Suspended' },
  ];

  selectedUser: UserItem | null = null;
  userForm: Partial<UserItem> = {};
  terminateConfirmUser: UserItem | null = null;
  profileTypeUser: UserItem | null = null;
  profileTypeValue: string | null = null;
  profileTypeOptions: ProfileTypeOption[] = [];

  userNotebooks: EnvironmentNotebooks[] = [];
  notebooksLoading = false;
  notebooksError: string | null = null;

  userUsage: UserUsage | null = null;
  usageLoading = false;
  usageError: string | null = null;
  private usersFilterDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private adminApiService: AdminApiService,
    private firestore: Firestore,
    private router: Router,
    snackBar: MatSnackBar,
    cdr: ChangeDetectorRef,
  ) {
    super(snackBar, cdr);
  }

  ngOnInit(): void {
    this.updateUserFilterValueOptions();
    this.refreshUsers();
    this.loadProfileTypes();
  }

  ngOnDestroy(): void {
    this.clearUsersFilterDebounce();
  }

  private loadProfileTypes(): void {
    collectionData(collection(this.firestore, 'profileTypes'), { idField: 'id' })
      .subscribe((actions: any[]) => {
        this.profileTypeOptions = (actions as RawProfileTypeOption[])
          .map((profile) => this.normalizeProfileTypeOption(profile))
          .filter((profile): profile is ProfileTypeOption => profile !== null)
          .sort((a, b) => a.id.localeCompare(b.id));
        this.updateUserFilterValueOptions();
      });
  }

  private normalizeProfileTypeOption(profile: RawProfileTypeOption): ProfileTypeOption | null {
    const id = typeof profile?.id === 'string' ? profile.id.trim().toLowerCase() : '';
    if (!id) {
      return null;
    }

    return {
      id,
      maxCredits: Number(profile?.maxCredits) || 0,
      maxMachines: Number(profile?.maxMachines) || 0,
      suspendHours: Number(profile?.suspendHours) || 0,
      maxIdleDays: Number(profile?.maxIdleDays) || 0,
    };
  }

  refreshUsers(): void {
    this.loadUsersPage(this.usersPage);
  }

  getDisplayedUsers(): UserItem[] {
    return this.users;
  }

  onUsersFilterFieldChange(): void {
    this.updateUserFilterValueOptions();

    if (!this.usesSelectableUserFilter()) {
      this.scheduleUsersFilter();
      return;
    }

    const allowedValues = new Set(this.userFilterValueOptions.map((option) => option.value));
    if (!allowedValues.has(this.usersFilterValue)) {
      this.usersFilterValue = '';
    }

    this.scheduleUsersFilter();
  }

  onUsersFilterValueChange(): void {
    this.scheduleUsersFilter();
  }

  applyUsersFilterImmediately(): void {
    this.clearUsersFilterDebounce();
    this.loadUsersPage(1);
  }

  clearUsersFilter(): void {
    this.usersFilterField = 'email';
    this.usersFilterValue = '';
    this.updateUserFilterValueOptions();
    this.clearUsersFilterDebounce();
    this.loadUsersPage(1);
  }

  usesSelectableUserFilter(): boolean {
    return this.usersFilterField === 'role'
      || this.usersFilterField === 'status'
      || this.usersFilterField === 'profileType';
  }

  private updateUserFilterValueOptions(): void {
    if (this.usersFilterField === 'role') {
      this.userFilterValueOptions = this.userRoleFilterOptions;
      return;
    }

    if (this.usersFilterField === 'status') {
      this.userFilterValueOptions = this.userStatusFilterOptions;
      return;
    }

    if (this.usersFilterField === 'profileType') {
      this.userFilterValueOptions = this.profileTypeOptions.map((profile) => ({
        value: profile.id,
        label: profile.id,
      }));
      return;
    }

    this.userFilterValueOptions = [];
  }

  getUserFilterPlaceholder(): string {
    switch (this.usersFilterField) {
      case 'email':
        return 'user@example.com';
      case 'id':
        return 'User ID';
      case 'company':
        return 'Company name';
      default:
        return 'Value';
    }
  }

  goToPreviousUsersPage(): void {
    if (this.usersPage > 1) {
      this.loadUsersPage(this.usersPage - 1);
    }
  }

  goToNextUsersPage(): void {
    if (this.usersPage < this.usersTotalPages) {
      this.loadUsersPage(this.usersPage + 1);
    }
  }

  private loadUsersPage(page: number): void {
    const requestedPage = Math.max(1, page);
    const filterValue = this.usersFilterValue.trim();
    this.trackApiCall(this.adminApiService.listUsers(this.token, {
      page: requestedPage,
      pageSize: this.usersPageSize,
      filterField: filterValue ? this.usersFilterField : undefined,
      filterValue: filterValue || undefined,
    })).subscribe({
      next: (result: UserListPage) => {
        this.users = result.items;
        this.usersPage = result.page;
        this.usersTotalItems = result.totalItems;
        this.usersTotalPages = result.totalPages;
      },
      error: (error) => this.handleError(error),
    });
  }

  private scheduleUsersFilter(): void {
    this.clearUsersFilterDebounce();
    this.usersFilterDebounceHandle = setTimeout(() => {
      this.usersFilterDebounceHandle = null;
      this.loadUsersPage(1);
    }, 1000);
  }

  private clearUsersFilterDebounce(): void {
    if (this.usersFilterDebounceHandle !== null) {
      clearTimeout(this.usersFilterDebounceHandle);
      this.usersFilterDebounceHandle = null;
    }
  }

  getUserDisplayName(user: UserItem): string {
    const name = [user.firstname, user.lastname].filter(Boolean).join(' ').trim();
    return name || user.email || '';
  }

  isUserAdmin(user: UserItem): boolean {
    return (user.role || '').toLowerCase() === 'admin';
  }

  toggleAdmin(user: UserItem): void {
    const makeAdmin = !this.isUserAdmin(user);
    const newRole = makeAdmin ? 'admin' : 'user';
    const actionKey = this.userUpdateActionKey(user.id);
    this.trackActionCall(actionKey, this.adminApiService.updateUser(this.token, user.id, { role: newRole })).subscribe({
      next: () => {
        user.role = newRole;
        if (this.selectedUser && this.selectedUser.id === user.id) {
          this.selectedUser.role = newRole;
          this.userForm.role = newRole;
        }
        this.showSuccess(makeAdmin ? `${this.getUserDisplayName(user)} is now an admin.` : `Admin role removed from ${this.getUserDisplayName(user)}.`);
      },
      error: (error) => this.handleError(error),
    });
  }

  openUserInfo(user: UserItem): void {
    this.selectedUser = user;
    this.userForm = { ...user };
    this.loadUserNotebooks(user.email);
    this.loadUserUsage(user.email);
  }

  closeUserInfo(): void {
    this.selectedUser = null;
    this.userForm = {};
    this.userNotebooks = [];
    this.notebooksError = null;
    this.notebooksLoading = false;
    this.userUsage = null;
    this.usageError = null;
    this.usageLoading = false;
  }

  private loadUserNotebooks(email: string): void {
    this.userNotebooks = [];
    this.notebooksError = null;
    if (!email) {
      return;
    }
    this.notebooksLoading = true;
    this.adminApiService.getUserNotebooks(this.token, email).pipe(take(1), timeout(this.apiCallTimeoutMs)).subscribe({
      next: (data) => this.applyState(() => {
        this.userNotebooks = data;
        this.notebooksLoading = false;
      }),
      error: (error) => this.applyState(() => {
        console.error('Failed to fetch user notebooks:', error);
        this.notebooksError = 'Unable to load notebook activity for this user.';
        this.notebooksLoading = false;
      }),
    });
  }

  private loadUserUsage(email: string): void {
    this.userUsage = null;
    this.usageError = null;
    if (!email) {
      return;
    }
    this.usageLoading = true;
    this.adminApiService.getUserUsage(this.token, email).pipe(take(1), timeout(this.apiCallTimeoutMs)).subscribe({
      next: (data) => this.applyState(() => {
        this.userUsage = data;
        this.usageLoading = false;
      }),
      error: (error) => this.applyState(() => {
        console.error('Failed to fetch user usage:', error);
        this.usageError = 'Unable to load usage history for this user.';
        this.usageLoading = false;
      }),
    });
  }

  saveUser(): void {
    if (!this.selectedUser) { return; }
    const id = this.selectedUser.id;
    const payload: Partial<UserItem> = {
      firstname: this.toNullableString(this.userForm.firstname) ?? '',
      lastname: this.toNullableString(this.userForm.lastname) ?? '',
      company: this.toNullableString(this.userForm.company) ?? '',
      title: this.toNullableString(this.userForm.title) ?? '',
      country: this.toNullableString(this.userForm.country) ?? '',
    };
    const actionKey = this.userUpdateActionKey(id);
    this.trackActionCall(actionKey, this.adminApiService.updateUser(this.token, id, payload)).subscribe({
      next: () => {
        const index = this.users.findIndex((u) => u.id === id);
        if (index > -1) {
          this.users[index] = { ...this.users[index], ...payload };
        }
        if (this.selectedUser) {
          this.selectedUser = { ...this.selectedUser, ...payload };
        }
        this.showSuccess('User profile updated successfully.');
      },
      error: (error) => this.handleError(error),
    });
  }

  confirmTerminateUser(user: UserItem): void {
    this.terminateConfirmUser = user;
  }

  cancelTerminateUser(): void {
    this.terminateConfirmUser = null;
  }

  terminateUser(): void {
    if (!this.terminateConfirmUser) { return; }
    const user = this.terminateConfirmUser;
    this.terminateConfirmUser = null;
    this.runUserMaintenance(user, 'Suspend', `Suspended ${this.getUserDisplayName(user)}.`, true);
  }

  reEnableUser(user: UserItem): void {
    this.runUserMaintenance(user, 'Re-Enable', `Re-enabled ${this.getUserDisplayName(user)}.`, false);
  }

  private runUserMaintenance(user: UserItem, type: UserMaintenanceType, successMessage: string, disabled: boolean): void {
    const actionKey = this.userMaintenanceActionKey(user.id);
    this.trackActionCall(actionKey, this.adminApiService.userMaintenance(this.token, user.id, type)).subscribe({
      next: () => {
        user.disabled = disabled;
        if (this.selectedUser && this.selectedUser.id === user.id) {
          this.selectedUser.disabled = disabled;
          this.userForm.disabled = disabled;
        }
        this.showSuccess(successMessage);
      },
      error: (error) => this.handleError(error),
    });
  }

  getUserCredits(user: UserItem): number {
    const value = Number(user.currentConsumption);
    return Number.isFinite(value) ? value : 0;
  }

  getProfileTypeId(user: UserItem): string {
    const raw = (user.profileType || '').toString().toLowerCase();
    return raw || 'free';
  }

  getProfileTypeOption(id: string | null | undefined): ProfileTypeOption | null {
    const key = (id || '').toString().toLowerCase();
    return this.profileTypeOptions.find((profile) => profile.id === key) || null;
  }

  getUserMaxCredits(user: UserItem): number | null {
    const profile = this.getProfileTypeOption(this.getProfileTypeId(user));
    if (!profile) {
      return null;
    }
    const value = Number(profile.maxCredits);
    return Number.isFinite(value) ? value : null;
  }

  openProfileType(user: UserItem): void {
    this.profileTypeUser = user;
    this.profileTypeValue = this.getProfileTypeId(user);
  }

  cancelProfileType(): void {
    this.profileTypeUser = null;
    this.profileTypeValue = null;
  }

  saveProfileType(): void {
    if (!this.profileTypeUser) { return; }
    const profileType = (this.profileTypeValue || '').toString().toLowerCase();
    if (!profileType) {
      this.showError('Please select a profile type.');
      return;
    }
    const user = this.profileTypeUser;
    const actionKey = this.userProfileActionKey(user.id);
    this.trackActionCall(actionKey, this.adminApiService.updateUser(this.token, user.id, { profileType })).subscribe({
      next: () => {
        const index = this.users.findIndex((u) => u.id === user.id);
        if (index > -1) {
          this.users[index] = { ...this.users[index], profileType };
        }
        if (this.selectedUser && this.selectedUser.id === user.id) {
          this.selectedUser = { ...this.selectedUser, profileType };
        }
        this.showSuccess(`Profile type for ${this.getUserDisplayName(user)} set to ${profileType}.`);
        this.profileTypeUser = null;
        this.profileTypeValue = null;
      },
      error: (error) => this.handleError(error),
    });
  }

  viewUserEnvironment(env: EnvironmentNotebooks): void {
    if (!this.selectedUser) { return; }
    this.router.navigate(['/admin/environments', this.selectedUser.id, env.environment]);
  }

  userUpdateActionKey(id: string): string { return `user-update:${id}`; }
  userMaintenanceActionKey(id: string): string { return `user-maintenance:${id}`; }
  userProfileActionKey(id: string): string { return `user-profile:${id}`; }
}
